import { useLocation, useRouter, useSearch } from '@tanstack/react-router'
import { useEffect } from 'react'

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  SpreadsheetBackground,
} from '@/components'
import { useAccountMutating, useCheckout } from '@/modules/account'
import {
  ACCOUNT_TYPES,
  AccountTypeToggle,
  FirmNameField,
  LegalFooter,
  PaymentTrust,
  type Plan,
  PlanInformation,
  SubmitPlan,
  singleProjectPlan,
  TermsCheckbox,
  useAuthMutating,
  useAuthMutations,
  useFetchPlans,
  usePlans,
} from '@/modules/auth'
import { useForm } from '@/stores'

export const CreateAccountPage = () => {
  const searchParams = useSearch({ strict: false })
  const location = useLocation()
  const router = useRouter()

  const { signOut } = useAuthMutations()
  const isAuthMutating = useAuthMutating()
  const isAccountMutating = useAccountMutating()
  const { createAccountCheckout } = useCheckout()
  const { data, errors, setFieldValue, setFieldError } = useForm()

  const name = searchParams.name || ''
  const priceId = searchParams.priceId || ''

  useFetchPlans()
  const plans = usePlans()

  const selectedPriceId = data?.priceId as string | undefined
  const selectedPlan = plans.find((plan) => plan.priceId === selectedPriceId)
  const isMutating = isAuthMutating || isAccountMutating

  const accountType = (data?.accountType as string) ?? ACCOUNT_TYPES.firm
  const isIndividual = accountType === ACCOUNT_TYPES.individual

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const params = new URL(location.url).searchParams

    if (!selectedPriceId) {
      setFieldError('priceId', 'Plan is required')
      return
    }

    params.set('name', data?.accountName as string)
    params.set('priceId', selectedPriceId)

    createAccountCheckout({
      priceId: selectedPriceId,
      name: data?.accountName as string,
      searchParams: params,
    })
  }

  const handleSignOut = async () => {
    await signOut()
    router.navigate({ to: '/login' })
  }

  useEffect(() => {
    if (priceId) {
      setFieldValue('priceId', priceId)
    }
    if (name) {
      setFieldValue('accountName', name)
    }
  }, [priceId, name])

  useEffect(() => {
    if (!selectedPriceId && plans.length > 0) {
      setFieldValue('priceId', singleProjectPlan(plans)?.priceId)
    }
  }, [plans, selectedPriceId])

  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-8 sm:py-10 relative overflow-hidden">
      <SpreadsheetBackground />

      <div className="relative z-10 mx-auto w-full max-w-5xl xl:max-w-6xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/logo.svg" alt="CounselPro" className="h-8 sm:h-9 w-auto" />
          <h1 className="mt-6 text-2xl sm:text-3xl font-semibold tracking-tight text-balance">
            Create a new account
          </h1>
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-muted-foreground text-pretty">
            Pay a flat fee for a single case that you can bill straight to your
            client. Running analysis across many matters? Firm-wide yearly plans
            are available.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 xl:gap-10 lg:items-start">
          <div className="lg:col-span-3">
            {selectedPlan && <PlanInformation plan={selectedPlan as Plan} />}
          </div>

          <Card className="lg:col-span-2 lg:sticky lg:top-8">
            <CardHeader className="space-y-1 pb-4">
              <h2 className="text-xl font-semibold tracking-tight text-center">
                Name your account
              </h2>
            </CardHeader>

            <CardContent className="px-4 sm:px-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <AccountTypeToggle
                  value={accountType}
                  setFieldValue={setFieldValue}
                />

                <FirmNameField
                  defaultValue={name}
                  error={errors?.accountName}
                  setFieldValue={setFieldValue}
                  label={isIndividual ? 'Your name' : 'Firm Name'}
                  placeholder={
                    isIndividual
                      ? 'Type your name'
                      : 'Type the name of your firm'
                  }
                  helpText="This name will appear on your dashboard and in billing correspondence."
                />

                <TermsCheckbox
                  value={data?.terms as boolean}
                  setFieldValue={setFieldValue}
                  isMutating={isMutating}
                  className="pt-2"
                />

                <SubmitPlan
                  agreedToTerms={data?.terms as boolean}
                  isMutating={isMutating}
                >
                  Create account
                </SubmitPlan>

                <PaymentTrust />
              </form>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3 pt-2 px-4 sm:px-6">
              <Button
                onClick={handleSignOut}
                className="w-full font-medium"
                theme="outline"
                size="lg"
              >
                Log out
              </Button>
            </CardFooter>
          </Card>
        </div>

        <LegalFooter />
      </div>
    </div>
  )
}
